import { diagnosticId } from './ess-diagnostic-event';
import { EssDiagnosticStore, StoredEssDiagnostic } from './ess-diagnostic-store';

/** Only committed records are eligible for sending. Unsaved records remain in memory. */
export class EssDiagnosticQueue {
    private pending = new Map<string, StoredEssDiagnostic>();
    private saving: Promise<boolean>;
    private busy = false;
    private failures = 0;
    private gapRecord: StoredEssDiagnostic;
    private context: StoredEssDiagnostic;
    private migrations: Array<{ ids: string[]; committed: () => void }> = [];

    constructor(private store: EssDiagnosticStore, private destination: string,
                private send: (records: StoredEssDiagnostic[]) => Promise<any>,
                private owner: string = diagnosticId(), private now: () => number = Date.now,
                private id: () => string = diagnosticId) {}

    enqueue(record: StoredEssDiagnostic): Promise<void> {
        this.context = record;
        this.pending.set(record.eventId, record);
        return this.persist();
    }

    migrate(records: StoredEssDiagnostic[], committed: () => void): Promise<void> {
        records.forEach(record => { this.context = record; this.pending.set(record.eventId, record); });
        this.migrations.push({ ids: records.map(record => record.eventId), committed });
        return this.persist();
    }

    private storageFailure(): void {
        this.failures++;
        if (this.gapRecord) { this.gapRecord.event.details.storageFailures = this.failures; }
    }

    private async persist(): Promise<void> {
        if (this.saving) {
            const active = this.saving;
            const success = await active;
            if (this.saving === active) { this.saving = null; }
            if (success && this.pending.size) { await this.persist(); }
            return;
        }
        if (this.failures && this.context && !this.gapRecord) {
            const eventId = this.id();
            this.gapRecord = Object.assign({}, this.context, { eventId,
                event: { schemaVersion: 1, eventId, sessionId: this.context.event.sessionId,
                    attemptId: this.context.event.attemptId, source: 'collector', sequence: 0,
                    timestamp: new Date(this.now()).toISOString(), elapsedMs: null,
                    eventType: 'collection-gap', collectorEpoch: 'durable-v1',
                    details: { scope: 'durable-queue', storageFailures: this.failures } } });
            this.pending.set(eventId, this.gapRecord);
        }
        const records = Array.from(this.pending.values());
        if (!records.length) { this.finishMigrations(); return; }
        this.saving = this.store.save(records).then(() => {
            records.forEach(record => this.pending.delete(record.eventId));
            if (this.gapRecord && records.some(record => record.eventId === this.gapRecord.eventId)) {
                this.gapRecord = null; this.failures = 0;
            }
            this.finishMigrations();
            return true;
        }).catch(() => { this.storageFailure(); return false; });
        const active = this.saving;
        try { await active; } finally { if (this.saving === active) { this.saving = null; } }
    }

    private finishMigrations(): void {
        this.migrations = this.migrations.filter(migration => {
            if (migration.ids.some(id => this.pending.has(id))) { return true; }
            try { migration.committed(); return false; } catch (_) { return true; }
        });
    }

    async flush(): Promise<void> {
        if (this.busy) { return; }
        this.busy = true;
        try {
            await this.persist();
            const records = await this.store.claim(this.destination, this.owner, this.now());
            if (!records.length) { return; }
            if (!this.context) { this.context = records[records.length - 1]; }
            try { await this.send(records); }
            catch (_) {
                await this.store.release(this.destination, this.owner, this.now());
                return;
            }
            await this.store.acknowledge(this.destination, this.owner, records.map(record => record.eventId), this.now());
        } catch (_) { this.storageFailure(); }
        finally { this.busy = false; }
    }
}
